import { type ReactElement, type ReactNode, useId } from 'react';
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import cx from 'clsx';
import { IconWarningSignOutlined } from '@repo/monochrome-icons';
import { CheckmarkOutlinedCheckboxIcon } from './icons/checkmark-outlined-checkbox';
import { MinusOutlinedCheckboxIcon } from './icons/minus-outlined-checkbox';
import styles from './checkbox.module.css';

interface CheckboxProps extends AriaCheckboxProps {
  children?: ReactNode;
  description?: ReactNode;
  errorMessage?: string;
}

export function Checkbox({
  className,
  children,
  description,
  errorMessage,
  'aria-describedby': ariaDescribedBy,
  ...props
}: CheckboxProps): ReactElement {
  const descriptionId = useId();

  return (
    <AriaCheckbox
      aria-describedby={description ? cx(ariaDescribedBy, descriptionId) : ariaDescribedBy}
      className={cx(
        className,
        styles['checkbox-wrapper'],
        errorMessage ? null : styles['checkbox-wrapper-error']
      )}
      data-test-checkbox=""
      {...props}
    >
      {({ isSelected, isIndeterminate, isInvalid }) => {
        let icon = null;
        if (isSelected) {
          icon = <CheckmarkOutlinedCheckboxIcon />;
        }

        if (isIndeterminate) {
          icon = <MinusOutlinedCheckboxIcon />;
        }

        return (
          <div className={styles['checkbox-container']}>
            <div className={styles['checkbox-row']}>
              <div
                className={cx('overlay', styles.checkbox, children ? styles['mt-2'] : null)}
                data-checkbox
              >
                {icon}
              </div>
              {children ? (
                <div className={styles['label-column']}>
                  <span className={cx('body-standard', styles.label)} data-label data-test-label="">
                    {children}
                  </span>
                  {description ? (
                    <span
                      className={cx('body-standard', styles.description)}
                      data-test-description=""
                      id={descriptionId}
                    >
                      {description}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            {isInvalid && errorMessage ? (
              <div className={styles.error} data-test-error-label="" role="alert">
                <IconWarningSignOutlined height={20} width={12} />
                <span className={styles.errorMessage}>{errorMessage}</span>
              </div>
            ) : null}
          </div>
        );
      }}
    </AriaCheckbox>
  );
}
